import React from 'react'
import type { ComponentProps, PartData } from '@enonic/react-components'
import VacanciesPrototype from '/react4xp/components/prototype/Vacancies'
import type { VacanciesProps, VacancyRow } from '/react4xp/components/prototype/Vacancies/types'
import { EditorPlaceholder } from '/react4xp/components/shared/EditorPlaceholder'

type ProcessorVacancy = {
	title: string
	applyUrl?: string
	pageUrl: string | null
}

type VacanciesPartData = {
	overline?: string
	title?: string
	intro?: string
	image?: { src: string; alt?: string }
	vacancies?: ProcessorVacancy[]
	viewAllLink?: { label: string; url: string; isExternal: boolean }
	applyNowLabel?: string
	readMoreLabel?: string
}

// Read more is always rendered by the prototype and its url is non-nullable, so a row
// whose page url did not resolve cannot be shown at all.
function toRow(vacancy: ProcessorVacancy): VacancyRow | null {
	if (!vacancy.pageUrl) {
		return null
	}

	return {
		title: vacancy.title,
		applicationUrl: vacancy.applyUrl,
		readMoreUrl: vacancy.pageUrl,
	}
}

export const Vacancies = ({ meta, data }: ComponentProps<PartData>) => {
	const {
		overline,
		title,
		intro,
		image,
		vacancies = [],
		viewAllLink,
		applyNowLabel,
		readMoreLabel,
	} = (data || {}) as VacanciesPartData

	const rows = vacancies.map(toRow).filter((row): row is VacancyRow => row !== null)

	// Unlike UpcomingEvents/RelatedArticles, zero rows is a valid rendering state here -
	// the section still carries its own copy, image and view-all link, and the prototype
	// only renders the row list behind its own `rows.length > 0` branch. Only a part with
	// nothing configured at all falls back to the edit-mode placeholder.
	const hasAnything = Boolean(overline || title || intro || image || rows.length > 0 || viewAllLink)
	if (!hasAnything) {
		return (
			<EditorPlaceholder
				mode={meta?.mode}
				componentClass="vacancies"
				title="Vacancies"
				reason={
					vacancies.length > 0
						? `${vacancies.length} vacancy/vacancies found, but none has a page URL.`
						: 'Nothing configured yet - add a heading, intro, image or a vacancies source.'
				}
			/>
		)
	}

	const props: VacanciesProps = {
		overline,
		title,
		intro,
		image: image && { src: image.src, alt: image.alt || title || '' },
		rows,
		viewAllLink: viewAllLink && {
			text: viewAllLink.label,
			url: viewAllLink.url,
			isExternal: viewAllLink.isExternal,
		},
		labels: { applyNowLabel, readMoreLabel },
	}

	return <VacanciesPrototype {...props} />
}
